import { IoArrowForward } from 'react-icons/io5';
import { MdOutlineCheckCircle, MdOutlinePayment } from 'react-icons/md';
import { TbSettings2, TbReceipt } from 'react-icons/tb';
import { EmployeeTaskSummary } from '../../redux/types/taskSheetTypes';

interface StatRowProps {
  icon: React.ReactNode;
  label: string;
  value: number;
  variant: 'dark' | 'green';
}

const StatRow: React.FC<StatRowProps> = ({ icon, label, value, variant }) => {
  const borderColor =
    variant === 'green' ? 'border-green-500 text-green-600' : 'border-gray-500 text-gray-600';
  const numColor = variant === 'green' ? 'text-green-500' : 'text-blue-900';

  return (
    <div className="flex items-center justify-between gap-2 py-1">
      <span
        className={`flex items-center gap-1.5 border rounded-full px-2.5 py-1 text-xs font-medium whitespace-nowrap ${borderColor}`}
      >
        {icon}
        {label}
      </span>
      <span className={`text-2xl font-bold ${numColor}`}>{value}</span>
    </div>
  );
};

interface Props {
  employee: EmployeeTaskSummary;
  onViewMore: (id: string) => void;
}

const EmployeeCard: React.FC<Props> = ({ employee, onViewMore }) => (
  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 flex flex-col gap-4">
    {/* Header */}
    <div className="flex items-center gap-4">
      <div className="relative flex-shrink-0">
        <div className="w-11 h-11 rounded-full bg-gradient-to-br from-orange-300 to-orange-500 flex items-center justify-center ring-2 ring-orange-100 overflow-hidden">
          {employee.profilePicture ? (
            <img src={employee.profilePicture} alt={employee.fullname} className="w-full h-full object-cover" />
          ) : (
            <span className="text-white text-base font-bold">{employee.fullname.charAt(0)}</span>
          )}
        </div>
        <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-gray-600 text-white text-[10px] font-bold rounded-full px-2 py-0.5 leading-none whitespace-nowrap">
          {employee.employeeCode}
        </div>
      </div>

      <div className="mt-1 flex-1">
        <p className="text-lg font-bold text-gray-800 leading-tight">{employee.fullname}</p>
        <p className="text-sm text-gray-400 mt-0.5">{employee.designation}</p>
      </div>

      <button
        onClick={() => onViewMore(employee.employeeStringId)}
        className="flex items-center gap-1.5 text-blue-600 text-sm font-semibold bg-blue-50 border border-blue-200 hover:bg-blue-100 hover:border-blue-300 transition-colors flex-shrink-0 px-3 py-1.5 rounded-full"
      >
        View more
        <IoArrowForward className="text-base" />
      </button>
    </div>

    {/* Stats */}
    <div className="flex gap-0">
      <div className="flex-1 flex flex-col gap-0.5 pr-4">
        <StatRow icon={<TbSettings2 />} label="Working on" value={employee.workingOnCount} variant="dark" />
        <StatRow icon={<MdOutlineCheckCircle />} label="Delivered" value={employee.deliveredCount} variant="green" />
      </div>
      <div className="w-px bg-gray-200 mx-1 self-stretch" />
      <div className="flex-1 flex flex-col gap-0.5 pl-4">
        <StatRow icon={<TbReceipt />} label="Billed" value={employee.billedCount} variant="green" />
        <StatRow icon={<MdOutlinePayment />} label="Payment Received" value={employee.paymentReceivedCount} variant="green" />
      </div>
    </div>
  </div>
);

export default EmployeeCard;
